import { Address } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { getAddressDisplayValue, padZeroLeft } from '@synergy/utils';

import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../autocomplete-list/pool-autocomplete-list';
import { PoolFormAutocompleteList, PoolFormAutocompleteListProps } from '../../abstract/form-autocomplete-list';
import { User } from '../../../models/user';

type AutocompleteType = 'DeliveryAddress' | 'InvoiceAddress';

interface PoolAddressAutocompleteListReduxProps {
    user: User | null;
}

interface PoolAddressAutocompleteListOwnProps extends PoolFormAutocompleteListProps<Address, PartialAddress> {
    autocompleteType: AutocompleteType;
    accountId?: number;
}

type PoolAddressAutocompleteListProps = PoolAddressAutocompleteListOwnProps & PoolAddressAutocompleteListReduxProps;

export interface PartialAddress extends BaseAutocompleteSuggestionType {
    street: string;
    zip: string;
}

class PoolAddressAutocompleteListBase extends PoolFormAutocompleteList<
    Address,
    PartialAddress,
    PoolAddressAutocompleteListProps
> {
    private messageId = {
        DeliveryAddress: 'app.label.deliveryAddress',
        InvoiceAddress: 'app.label.invoiceAddress',
    };

    public override label = (<FormattedMessage id={this.messageId[this.props.autocompleteType]} />);

    private getFilteredAddresses = (items: Address[]) => {
        switch (this.props.autocompleteType) {
            case 'DeliveryAddress': {
                return items.filter(address => address.isDeliveryAddress);
            }
            case 'InvoiceAddress': {
                return items.filter(address => address.isInvoiceAddress);
            }
            default:
                return [];
        }
    };

    public getSuggestionValue = (suggestion: PartialAddress) => suggestion && `${suggestion.street}, ${suggestion.zip}`;

    public override getDisplayValue(address: Address) {
        return getAddressDisplayValue(address);
    }

    public getPartial = (items: Address[]): PartialAddress[] => {
        return this.getFilteredAddresses(items).map(address => {
            return {
                id: padZeroLeft(address.id, 5),
                title: address.title ?? '',
                street: [address.street, address.number, address.addition].filter(Boolean).join(' '),
                zip: `${address.zip} ${address.city}`,
            };
        });
    };
}

const mapStateToProps = (state: AppState) => ({
    user: state.user,
});

export const PoolAddressAutocompleteList = connect(mapStateToProps)(PoolAddressAutocompleteListBase);
